/**
 * CampaignTemplate - Estilo campanha eleitoral, número em destaque, forte uso de cores do tenant
 * Hero com número gigante, cards de propostas com ícones, CTAs visíveis
 */

import {
  Hash,
  Award,
  Target,
  Trophy,
  FileText,
  Image,
  CheckCircle,
  MapPin,
  Megaphone,
  Star,
} from 'lucide-react';
import { SantinhoTemplateProps, hasSocialLinks } from './types';
import { SocialLinks } from './SocialLinks';
import { GallerySection } from './GallerySection';
import { ApprovedProjectsSection } from './ApprovedProjectsSection';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';

export function CampaignTemplate({ tenant }: SantinhoTemplateProps) {
  const { publicPage, theme, name } = tenant;
  const { title, description, content, socialLinks } = publicPage;

  const SectionTitle = ({
    icon: Icon,
    children,
  }: {
    icon: React.ElementType;
    children: React.ReactNode;
  }) => (
    <div className="flex items-center gap-3 mb-7">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
        style={{ backgroundColor: theme.primaryColor }}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h2
        className="text-2xl font-black uppercase tracking-tight"
        style={{ color: theme.primaryColor }}
      >
        {children}
      </h2>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-gray-100"
      style={{ fontFamily: theme.fontFamily || 'inherit' }}
    >
      {/* Hero Section */}
      <header
        className="relative overflow-hidden"
        style={{ backgroundColor: theme.primaryColor }}
      >
        {/* Diagonal stripe pattern */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 12px,
              white 12px,
              white 24px
            )`,
          }}
        />

        {/* Cover photo overlay */}
        {content.coverPhoto && (
          <img
            src={content.coverPhoto}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay"
          />
        )}

        {/* Top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1.5"
          style={{
            background: `linear-gradient(to right, ${theme.secondaryColor}, white, ${theme.secondaryColor})`,
            opacity: 0.6,
          }}
        />

        <div className="container mx-auto px-4 py-14 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">

            {/* Left: Photo + Name */}
            <div className="flex flex-col md:flex-row items-center gap-7">
              {/* Photo */}
              <div className="relative">
                {content.candidatePhoto ? (
                  <>
                    <div
                      className="absolute -inset-2 rounded-full opacity-30 blur-md"
                      style={{ backgroundColor: 'white' }}
                    />
                    <img
                      src={content.candidatePhoto}
                      alt={name}
                      className="relative w-40 h-40 md:w-52 md:h-52 rounded-full object-cover border-4 border-white shadow-2xl"
                    />
                  </>
                ) : (
                  <div className="w-40 h-40 md:w-52 md:h-52 rounded-full border-4 border-white shadow-2xl flex items-center justify-center text-6xl font-bold bg-white/20 text-white backdrop-blur-sm">
                    {name.charAt(0)}
                  </div>
                )}
                {/* Star badge */}
                <div
                  className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full border-2 border-white flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: theme.secondaryColor }}
                >
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
              </div>

              {/* Name and Slogan */}
              <div className="text-center md:text-left text-white">
                <h1 className="text-4xl md:text-6xl font-black uppercase leading-tight drop-shadow-lg">
                  {title || name}
                </h1>
                {description && (
                  <p className="mt-2 text-lg md:text-xl text-white/85 font-semibold max-w-md">
                    {description}
                  </p>
                )}
                {/* Social Links */}
                {hasSocialLinks(socialLinks) && (
                  <div className="mt-5">
                    <SocialLinks links={socialLinks} color="rgba(255,255,255,0.2)" />
                  </div>
                )}
              </div>
            </div>

            {/* Right: Vote Number */}
            {content.candidateNumber && (
              <div className="text-center flex-shrink-0">
                <div className="bg-white rounded-3xl px-8 py-6 shadow-2xl ring-4 ring-white/30">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Hash className="w-4 h-4" style={{ color: theme.primaryColor }} />
                    <p
                      className="text-sm font-black uppercase tracking-[0.2em]"
                      style={{ color: theme.primaryColor }}
                    >
                      Vote
                    </p>
                  </div>
                  <p
                    className="text-7xl md:text-8xl font-black leading-none tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    {content.candidateNumber}
                  </p>
                  <div
                    className="w-12 h-1 rounded-full mx-auto mt-3"
                    style={{ backgroundColor: theme.primaryColor }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 90"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 90L48 82C96 74 192 58 288 50C384 42 480 42 576 46C672 50 768 58 864 62C960 66 1056 66 1152 62C1248 58 1344 50 1392 46L1440 42V90H1392C1344 90 1248 90 1152 90C1056 90 960 90 864 90C768 90 672 90 576 90C480 90 384 90 288 90C192 90 96 90 48 90H0Z"
              fill="#f3f4f6"
            />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Biography */}
          {content.biography && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Megaphone}>Sobre</SectionTitle>
              <div className="space-y-4">
                {content.biography.split('\n').filter(Boolean).map((p, i) => (
                  <p key={i} className="text-gray-700 leading-relaxed text-lg">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          )}

          {/* Mandates */}
          {content.mandates && content.mandates.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Award}>Experiência Política</SectionTitle>
              <div className="grid md:grid-cols-2 gap-4">
                {content.mandates.map((mandate, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl border-2 hover:shadow-sm transition-shadow"
                    style={{ borderColor: `${theme.primaryColor}30`, backgroundColor: `${theme.primaryColor}06` }}
                  >
                    <p className="font-bold text-base" style={{ color: theme.primaryColor }}>
                      {mandate.position}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-gray-500 text-sm">
                        {mandate.location} &middot; {mandate.year}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Proposals */}
          {content.proposals && content.proposals.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Target}>Propostas</SectionTitle>
              <div className="space-y-4">
                {content.proposals.map((proposal, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-5 p-5 rounded-xl hover:bg-gray-50 transition-colors"
                    style={{ backgroundColor: `${theme.primaryColor}08` }}
                  >
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black text-white shadow-md"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-gray-700 text-lg pt-2.5 leading-relaxed">{proposal}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Achievements */}
          {content.achievements && content.achievements.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Trophy}>Conquistas</SectionTitle>
              <div className="grid md:grid-cols-2 gap-4">
                {content.achievements.map((achievement, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <CheckCircle
                      className="w-5 h-5 mt-0.5 flex-shrink-0"
                      style={{ color: theme.primaryColor }}
                    />
                    <p className="text-gray-700 leading-relaxed">{achievement}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Approved Projects */}
          {content.approvedProjects && content.approvedProjects.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={FileText}>Projetos Aprovados</SectionTitle>
              <ApprovedProjectsSection
                projects={content.approvedProjects}
                primaryColor={theme.primaryColor}
                variant="cards"
              />
            </section>
          )}

          {/* Gallery */}
          {content.gallery && content.gallery.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Image}>Galeria</SectionTitle>
              <GallerySection images={content.gallery} />
            </section>
          )}

          {/* Social Links */}
          {hasSocialLinks(socialLinks) && (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 md:p-9">
              <SectionTitle icon={Megaphone}>Redes Sociais</SectionTitle>
              <SocialLinks links={socialLinks} color={theme.primaryColor} />
            </section>
          )}
        </div>
      </main>

      {/* Footer with Vote CTA */}
      <footer style={{ backgroundColor: theme.secondaryColor }}>
        {/* Vote reminder bar */}
        {content.candidateNumber && (
          <div
            className="py-8 text-center"
            style={{ backgroundColor: theme.primaryColor }}
          >
            <p className="text-white text-2xl font-black uppercase tracking-wide drop-shadow">
              Vote{' '}
              <span
                className="text-5xl font-black bg-white px-4 py-1 rounded-xl mx-2"
                style={{ color: theme.primaryColor }}
              >
                {content.candidateNumber}
              </span>
            </p>
          </div>
        )}

        {/* Credits */}
        <div className="py-6">
          <div className="container mx-auto px-4 text-center">
            <p className="text-white/70 text-sm">
              Página criada com{' '}
              <a
                href="https://campanhaflow.com.br"
                className="text-white hover:underline font-bold"
              >
                CampanhaFlow
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Registration Button */}
      <ContactRegistrationButton />

      {/* Webchat widget */}
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
